import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { BookOpenText, PenLine } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { GoogleAuthButton } from "../components/GoogleAuthButton";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [role, setRole] = useState("reader");
  const [form, setForm] = useState({ email: "", password: "", display_name: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register({ ...form, role });
      toast.success(`Welcome to Faraway Sister Haven, ${u.display_name}!`);
      nav(u.role === "author" ? "/author" : "/reader");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-0 py-16">
      <div className="paper-card p-8">
        <div className="overline mb-2">Join the haven</div>
        <h1 className="rainbow-text text-3xl mb-6">Create your account</h1>

        <GoogleAuthButton testId="register-google-btn" />
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px" style={{ background: "var(--border-soft)" }} />
          <span className="text-xs" style={{ color: "var(--ink-muted)" }}>or with email</span>
          <div className="flex-1 h-px" style={{ background: "var(--border-soft)" }} />
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6">
          <button
            type="button"
            onClick={() => setRole("reader")}
            className={`btn-press rounded-xl border p-4 text-left ${role === "reader" ? "border-indigo-800 bg-indigo-50/60" : "border-slate-200"}`}
            data-testid="register-role-reader"
          >
            <BookOpenText size={18} className="text-indigo-800 mb-2" />
            <div className="font-serif text-lg">Reader</div>
            <div className="text-xs text-slate-600">Discover &amp; apply to ARCs</div>
          </button>
          <button
            type="button"
            onClick={() => setRole("author")}
            className={`btn-press rounded-xl border p-4 text-left ${role === "author" ? "border-amber-700 bg-amber-50/60" : "border-slate-200"}`}
            data-testid="register-role-author"
          >
            <PenLine size={18} className="text-amber-700 mb-2" />
            <div className="font-serif text-lg">Author</div>
            <div className="text-xs text-slate-600">Post &amp; manage campaigns</div>
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div>
            <Label htmlFor="display_name">{role === "author" ? "Author/Pen name (public)" : "Display name"}</Label>
            <Input id="display_name" required minLength={1} value={form.display_name} onChange={set("display_name")} data-testid="register-name-input" />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="register-email-input" />
          </div>
          <div>
            <Label htmlFor="password">Password (min 6)</Label>
            <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="register-password-input" />
          </div>
          <Button type="submit" disabled={loading} className="w-full btn-press btn-rainbow" data-testid="register-submit-btn">
            {loading ? "Creating…" : `Create ${role} account`}
          </Button>
        </form>
        <p className="text-sm text-slate-600 mt-6 text-center">
          Already have an account?{" "}
          <Link to="/login" className="text-indigo-800 underline underline-offset-2" data-testid="register-goto-login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
